import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useQuery, useMutation, useLazyQuery } from '@apollo/client';
import { LIST_SCENARIOS, LIST_CUSTOM_EVALUATION_RULES } from '../../graphql/queries';
import { CREATE_SCENARIO, UPDATE_SCENARIO, DELETE_SCENARIO, CREATE_MATERIAL, DELETE_MATERIAL, UPDATE_SCENARIO_ORDER, BULK_UPDATE_SCENARIOS } from '../../graphql/mutations';
import { Scenario, Material } from '../../types/models';
import { v4 as uuidv4 } from 'uuid';
import { uploadService } from '../../services/uploadService';
import { 
  PDF_VALIDATIONS,
  SCENARIO_VALIDATIONS,
  validatePdfFiles,
  extractTextFromPdf,
  validatePdfTextLength
} from '../../utils/validationUtils';
import {
  Box,
  Typography,
  Paper,
  Button,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  Chip,
  FormControlLabel,
  Switch,
  CircularProgress,
  RadioGroup,
  Radio,
  Alert,
  useTheme,
  Tooltip,
  Menu,
  MenuItem,
  Badge
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  DragIndicator as DragIndicatorIcon,
  Download as DownloadIcon,
  Rule as RuleIcon,
  FileUpload as FileUploadIcon,
  FileDownload as FileDownloadIcon,
} from '@mui/icons-material';
import { parseCsv, parseMultiValue, escapeCsvField, stringifyMultiValue } from '../../utils/csvUtils';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { canAccess } from '../../utils/permissions';
import type { DropResult } from '@hello-pangea/dnd';
import ScenarioFormDialog, { type ScenarioFormPayload } from './components/ScenarioFormDialog';
import ScenarioTable from './components/ScenarioTable';
import CustomEvaluationRulesDialog from './components/CustomEvaluationRulesDialog';

/** CSV 一括更新／ダウンロードで使用する列定義（label=日本語ヘッダー、field=内部キー） */
const SCENARIO_CSV_COLUMNS = [
  { label: 'シナリオID',                   field: 'scenarioId' },
  { label: 'タイトル',                     field: 'title' },
  { label: '説明',                         field: 'description' },
  { label: '目的',                         field: 'goal' },
  { label: '目安時間（分・最大30分）',                  field: 'estimatedTime' },
  { label: '資料をオリエンテーションで表示（する|しない）', field: 'showMaterialsOnOrientation' },
  { label: '相手の名前',                   field: 'targetName' },
  { label: '相手の性別',                   field: 'targetGender' },
  { label: '相手の年代',                   field: 'targetAge' },
  { label: '相手の言語',                   field: 'targetLanguage' },
  { label: '状況設定',                     field: 'situation' },
  { label: '状況設定（クローズ）',           field: 'situationClosed' },
  { label: '利用できる所属',               field: 'department' },
  { label: 'タグ',                         field: 'tags' },
  { label: '公開状態（公開|非公開）',                     field: 'isActive' },
] as const;
const SCENARIO_CSV_FIELD_SET = new Set(SCENARIO_CSV_COLUMNS.map((c) => c.field));
const SCENARIO_CSV_LABEL_TO_FIELD: Record<string, string> = Object.fromEntries(
  SCENARIO_CSV_COLUMNS.map((c) => [c.label, c.field])
);

function sortScenariosByDisplayOrder(items: Scenario[]): Scenario[] {
  return [...items].sort((a, b) => {
    if (a.displayOrder === undefined && b.displayOrder === undefined) {
      return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
    }
    if (a.displayOrder === undefined) return -1;
    if (b.displayOrder === undefined) return 1;
    if (a.displayOrder !== b.displayOrder) {
      return a.displayOrder - b.displayOrder;
    }
    return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
  });
}

// 性別の選択肢
const genderOptions = ['男性', '女性'];
// 年代の選択肢
const ageOptions = ['20～30代', '40～50代'];
// 言語の選択肢
const languageOptions = ['日本語', '英語'];

const ScenarioManagementPage: React.FC = () => {
  const theme = useTheme();
  
  // Reduxからユーザー情報を取得
  const { currentUser } = useSelector((state: RootState) => state.user);
  const companyId = currentUser?.companyId || '';
  const userId = currentUser?.userId || '';

  // 権限フラグ
  const permCsvDownload = canAccess(currentUser, 'scenarioMgmt.view.canCsvDownload');
  const permCreateNew = canAccess(currentUser, 'scenarioMgmt.edit.canCreateNew');
  const permCsvImport = canAccess(currentUser, 'scenarioMgmt.edit.canCsvImport');
  const permUpdate = canAccess(currentUser, 'scenarioMgmt.edit.canUpdate');
  const permDelete = canAccess(currentUser, 'scenarioMgmt.edit.canDelete');
  const permCustomScenarioUpdate = canAccess(currentUser, 'scenarioMgmt.edit.canCustomScenarioUpdate');

  // 表示件数
  const DISPLAY_INCREMENT = 100;

  // 状態管理
  const [filteredScenarios, setFilteredScenarios] = useState<Scenario[]>([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openViewDialog, setOpenViewDialog] = useState(false);
  const [openTemplateDialog, setOpenTemplateDialog] = useState(false);
  const [currentScenario, setCurrentScenario] = useState<Scenario | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  
  // 作成・編集ダイアログ保存中の状態（アップロード中など）
  const [uploadingFiles, setUploadingFiles] = useState(false);

  // カスタム評価ルール管理用の状態
  const [openCustomEvaluationDialog, setOpenCustomEvaluationDialog] = useState(false);
  const [customEvaluationScenario, setCustomEvaluationScenario] = useState<Scenario | null>(null);
  const [scenarioRuleCounts, setScenarioRuleCounts] = useState<Record<string, number>>({});

  // GraphQLクエリを実行
  const { loading, error, data, fetchMore } = useQuery(LIST_SCENARIOS, {
    variables: { companyId, limit: DISPLAY_INCREMENT },
    fetchPolicy: 'cache-and-network'
  });
  
  // GraphQLミューテーションを実行
  const [createScenario, { loading: createLoading }] = useMutation(CREATE_SCENARIO);
  const [updateScenario, { loading: updateLoading }] = useMutation(UPDATE_SCENARIO);
  const [updateScenarioOrder] = useMutation(UPDATE_SCENARIO_ORDER);
  const [deleteScenario, { loading: deleteLoading }] = useMutation(DELETE_SCENARIO);
  const [bulkUpdateScenarios, { loading: bulkLoading }] = useMutation(BULK_UPDATE_SCENARIOS);

  // CSV インポート関連の状態
  const [openBulkDialog, setOpenBulkDialog] = useState(false);
  const [openBulkConfirmDialog, setOpenBulkConfirmDialog] = useState(false);
  const [openBulkResultDialog, setOpenBulkResultDialog] = useState(false);
  const [bulkRows, setBulkRows] = useState<Array<Record<string, any>>>([]);
  const [bulkPreview, setBulkPreview] = useState<string[][]>([]);
  const [bulkErrors, setBulkErrors] = useState<string[]>([]);
  const [bulkResult, setBulkResult] = useState<null | { successCount: number; errorCount: number; errors: Array<{ scenarioId: string; message: string }> }>(null);
  const bulkFileRef = useRef<HTMLInputElement>(null);
  const [csvDownloadLoading, setCsvDownloadLoading] = useState(false);
  const [createMaterial] = useMutation(CREATE_MATERIAL);
  const [deleteMaterial] = useMutation(DELETE_MATERIAL);
  const [getCustomEvaluationRules] = useLazyQuery(LIST_CUSTOM_EVALUATION_RULES);

  // シナリオ一覧
  const scenarios = data?.listScenarios?.items || [];
  const nextToken = data?.listScenarios?.nextToken;
  
  // データが取得されたらfilteredScenariosステートを更新
  React.useEffect(() => {
    if (data?.listScenarios?.items) {
      let sortedScenarios = sortScenariosByDisplayOrder(data.listScenarios.items);

      // managementScope によるフィルタリング（ブランクの場合は全件表示）
      const scope = currentUser?.managementScope?.trim();
      if (scope) {
        sortedScenarios = sortedScenarios.filter((s) => s.department === scope);
      }

      setFilteredScenarios(sortedScenarios);
      
      // 各シナリオのカスタム評価ルール数を取得
      const fetchRuleCounts = async () => {
        const counts: Record<string, number> = {};
        const promises = sortedScenarios.map(async (scenario) => {
          try {
            const { data } = await getCustomEvaluationRules({
              variables: { parentScenarioId: scenario.scenarioId, limit: 100 }
            });
            counts[scenario.scenarioId] = data?.listCustomEvaluationRules?.items?.length || 0;
          } catch (error) {
            console.error(`Error fetching rule count for scenario ${scenario.scenarioId}:`, error);
            counts[scenario.scenarioId] = 0;
          }
        });
        
        await Promise.all(promises);
        setScenarioRuleCounts(counts);
      };
      
      fetchRuleCounts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, currentUser?.managementScope]);

  // さらに取得ボタンのクリックハンドラー（useCallback で安定化）
  const handleLoadMore = useCallback(() => {
    if (nextToken) {
      fetchMore({
        variables: { companyId, limit: DISPLAY_INCREMENT, nextToken },
        updateQuery: (prev, { fetchMoreResult }) => {
          if (!fetchMoreResult) return prev;
          return {
            listScenarios: {
              __typename: prev.listScenarios.__typename,
              items: [...prev.listScenarios.items, ...fetchMoreResult.listScenarios.items],
              nextToken: fetchMoreResult.listScenarios.nextToken
            }
          };
        }
      });
    }
  }, [nextToken, fetchMore, companyId]);

  // 編集ダイアログを開く（useCallback で安定化）
  const handleOpenDialog = useCallback((scenario?: Scenario) => {
    setCurrentScenario(scenario ?? null);
    setOpenDialog(true);
  }, []);

  // 編集ダイアログを閉じる
  const handleCloseDialog = useCallback(() => {
    setOpenDialog(false);
    setCurrentScenario(null);
  }, []);

  // 削除ダイアログを開く
  const handleOpenDeleteDialog = useCallback((scenario: Scenario) => {
    setCurrentScenario(scenario);
    setOpenDeleteDialog(true);
  }, []);

  // 削除ダイアログを閉じる
  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
  };

  // ファイルアップロード処理（シナリオ保存時にダイアログから渡されたファイルをアップロード）
  const uploadFiles = async (scenarioId: string, files: File[]) => {
    if (files.length === 0) return;
    
    try {
      for (const file of files) {
        console.log(`ファイル "${file.name}" (${file.size} bytes) をアップロード中...`);
        
        // PDFからテキストを抽出
        const extractedText = await extractTextFromPdf(file);
        
        // S3にアップロードするためのファイル名を生成（一意のIDを使用）
        const fileId = uuidv4();
        const fileExtension = file.name.split('.').pop() || 'pdf';
        const s3Key = `materials/${companyId}/${scenarioId}/${fileId}.${fileExtension}`;
        
        // 署名付きURLを取得
        const uploadUrl = await uploadService.getUploadUrl(
          companyId,
          s3Key,
          file.name,
          file.type,
          scenarioId
        );
        
        // 署名付きURLを使用してファイルをアップロード
        await uploadService.uploadFileWithPresignedUrl(uploadUrl, file);
        
        console.log(`ファイル "${file.name}" をS3にアップロードしました: ${s3Key}`);
        
        // GraphQLミューテーションを使用して資料情報をデータベースに登録
        const materialResult = await createMaterial({
          variables: {
            input: {
              fileId,
              companyId,
              parentScenarioId: scenarioId,
              name: file.name,
              type: file.type,
              pdfPath: s3Key
            }
          },
          refetchQueries: [
            { query: LIST_SCENARIOS, variables: { companyId, limit: DISPLAY_INCREMENT } }
          ]
        });
      }
      
      console.log('すべてのファイルのアップロードが完了しました');
    } catch (error: any) {
      console.error('ファイルアップロード中にエラーが発生しました:', error);
      throw error; // エラーを上位に伝播させる
    }
  };

  // 作成・編集ダイアログからの保存（payload を受け取りミューテーション・アップロードを実行）
  const handleSaveScenarioFromDialog = async (payload: ScenarioFormPayload) => {
    const { formData: fd, uploadedFiles: files, materialsToDelete: toDelete, isActive } = payload;
    const tagsArray = fd.tags.split(',').map(tag => tag.trim()).filter(tag => tag.length > 0);
    setUploadingFiles(true);
    try {
      let scenarioId: string;
      if (currentScenario) {
        await updateScenario({
          variables: {
            input: {
              scenarioId: currentScenario.scenarioId,
              title: fd.title,
              description: fd.description,
              goal: fd.goal,
              estimatedTime: fd.estimatedTime,
              situation: fd.situation,
              targetName: fd.targetName,
              targetGender: fd.targetGender,
              targetAge: fd.targetAge,
              targetLanguage: fd.targetLanguage,
              tags: tagsArray,
              department: fd.department,
              isActive,
              showMaterialsOnOrientation: fd.showMaterialsOnOrientation,
              situationClosed: fd.situationClosed
            }
          },
          refetchQueries: [{ query: LIST_SCENARIOS, variables: { companyId, limit: DISPLAY_INCREMENT } }]
        });
        scenarioId = currentScenario.scenarioId;
        for (const materialId of toDelete) {
          try {
            await deleteMaterial({
              variables: { materialId },
              refetchQueries: [{ query: LIST_SCENARIOS, variables: { companyId, limit: DISPLAY_INCREMENT } }]
            });
          } catch (err) {
            console.error('資料削除エラー:', err);
          }
        }
      } else {
        const result = await createScenario({
          variables: {
            input: {
              companyId,
              title: fd.title,
              description: fd.description,
              goal: fd.goal,
              estimatedTime: fd.estimatedTime,
              situation: fd.situation,
              targetName: fd.targetName,
              targetGender: fd.targetGender,
              targetAge: fd.targetAge,
              targetLanguage: fd.targetLanguage,
              tags: tagsArray,
              department: fd.department,
              isActive,
              showMaterialsOnOrientation: fd.showMaterialsOnOrientation,
              situationClosed: fd.situationClosed
            }
          },
          refetchQueries: [{ query: LIST_SCENARIOS, variables: { companyId, limit: DISPLAY_INCREMENT } }]
        });
        scenarioId = result.data.createScenario.scenarioId;
      }
      await uploadFiles(scenarioId, files);
      handleCloseDialog();
    } catch (error: unknown) {
      console.error('Error saving scenario:', error);
      throw error;
    } finally {
      setUploadingFiles(false);
    }
  };

  // シナリオ削除ハンドラー
  const handleDeleteScenario = async () => {
    if (currentScenario) {
      try {
        await deleteScenario({
          variables: { scenarioId: currentScenario.scenarioId },
          refetchQueries: [
            { query: LIST_SCENARIOS, variables: { companyId, limit: DISPLAY_INCREMENT } }
          ]
        });
        handleCloseDeleteDialog();
      } catch (error: any) {
        console.error('Error deleting scenario:', error);
        // エラー処理を追加
      }
    }
  };

  // ドラッグアンドドロップハンドラー（useCallback で安定化）
  const handleDragEnd = useCallback(
    async (result: DropResult) => {
      if (!result.destination) return;
      if (nextToken) {
        setErrorMessage('未取得のレコードがある状態では並び替えできません。「さらに表示する」ボタンで全件を表示してから並び替えを行ってください。');
        return;
      }
      const items = Array.from(filteredScenarios);
      const [reorderedItem] = items.splice(result.source.index, 1);
      items.splice(result.destination.index, 0, reorderedItem);
      setFilteredScenarios(items);
      try {
        await updateScenarioOrder({
          variables: { input: { companyId, scenarioOrders: items.map((s, i) => ({ scenarioId: s.scenarioId, displayOrder: i })) } },
          refetchQueries: [{ query: LIST_SCENARIOS, variables: { companyId, limit: DISPLAY_INCREMENT } }]
        });
      } catch (err) {
        console.error('順序の保存中にエラーが発生しました:', err);
        setFilteredScenarios(data?.listScenarios?.items || []);
        setErrorMessage('順序の保存中にエラーが発生しました。');
      }
    },
    [filteredScenarios, nextToken, data?.listScenarios?.items, updateScenarioOrder, companyId]
  );

  // 日付フォーマット関数（日付のみ）
  const formatDate = useCallback((dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ja-JP', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  }, []);

  // 完全な日時フォーマット関数（マウスオーバー用）
  const formatFullDateTime = useCallback((dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ja-JP', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  }, []);

  // 公開状態トグル（テーブル用・useCallback で安定化）
  const handleToggleActive = useCallback(
    async (scenario: Scenario, checked: boolean) => {
      try {
        await updateScenario({
          variables: { input: { scenarioId: scenario.scenarioId, isActive: checked } },
          refetchQueries: [{ query: LIST_SCENARIOS, variables: { companyId, limit: DISPLAY_INCREMENT } }]
        });
      } catch (err) {
        console.error('Error updating scenario status:', err);
      }
    },
    [updateScenario, companyId]
  );

  // シナリオダウンロードハンドラー（useCallback で安定化）
  const handleDownloadScenario = useCallback(
    async (scenario: Scenario) => {
      try {
        let customEvaluationRules: Array<{ name: string; content: string; displayOrder?: number }> = [];
        try {
          const { data } = await getCustomEvaluationRules({
            variables: { parentScenarioId: scenario.scenarioId, limit: 100 }
          });
          if (data?.listCustomEvaluationRules?.items) {
            const sortedRules = [...data.listCustomEvaluationRules.items].sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0));
            customEvaluationRules = sortedRules.map(rule => ({ name: rule.name, content: rule.content, displayOrder: rule.displayOrder }));
          }
        } catch {
          // エラーが発生してもダウンロードは続行
        }
        const downloadData = {
          title: scenario.title,
          department: scenario.department || '',
          description: scenario.description,
          goal: scenario.goal,
          estimatedtime: scenario.estimatedTime || 15,
          situation: scenario.situation || '',
          situationClosed: scenario.situationClosed || '',
          showMaterialsOnOrientation: scenario.showMaterialsOnOrientation !== false,
          targetname: scenario.targetName || '',
          targetgender: scenario.targetGender || '',
          targetage: scenario.targetAge || '',
          targetlanguage: scenario.targetLanguage || '',
          tags: scenario.tags || [],
          customEvaluationRules
        };
        const jsonString = JSON.stringify(downloadData, null, 2);
        const blob = new Blob([jsonString], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const sanitizedTitle = scenario.title.replace(/[<>:"/\\|?*]/g, '').replace(/\s+/g, '_');
        const link = document.createElement('a');
        link.href = url;
        link.download = `scenario_${sanitizedTitle}.txt`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      } catch (err) {
        console.error('シナリオのダウンロード中にエラーが発生しました:', err);
        setErrorMessage('シナリオのダウンロード中にエラーが発生しました。');
      }
    },
    [getCustomEvaluationRules]
  );

  // カスタム評価ルール管理ダイアログを開く（useCallback で安定化）
  const handleOpenCustomEvaluationDialog = useCallback((scenario: Scenario) => {
    setCustomEvaluationScenario(scenario);
    setOpenCustomEvaluationDialog(true);
  }, []);

  // カスタム評価ルール数更新（ダイアログから通知）
  const handleCustomEvaluationRulesUpdated = useCallback((scenarioId: string, count: number) => {
    setScenarioRuleCounts((prev) => ({ ...prev, [scenarioId]: count }));
  }, []);

  // カスタム評価ルール管理ダイアログを閉じる
  const handleCloseCustomEvaluationDialog = useCallback(() => {
    setOpenCustomEvaluationDialog(false);
    setCustomEvaluationScenario(null);
  }, []);

  // CSV インポートダイアログを開く／閉じる
  const handleOpenBulkDialog = () => {
    setBulkRows([]);
    setBulkPreview([]);
    setBulkErrors([]);
    setBulkResult(null);
    setOpenBulkDialog(true);
  };
  const handleCloseBulkDialog = () => {
    if (bulkLoading) return;
    setOpenBulkDialog(false);
    setBulkRows([]);
    setBulkPreview([]);
    setBulkErrors([]);
    if (bulkFileRef.current) bulkFileRef.current.value = '';
  };
  const handleCloseBulkConfirmDialog = () => {
    if (bulkLoading) return;
    setOpenBulkConfirmDialog(false);
    setBulkRows([]);
    setBulkPreview([]);
    setBulkErrors([]);
    if (bulkFileRef.current) bulkFileRef.current.value = '';
  };
  const handleCloseBulkResultDialog = () => {
    setOpenBulkResultDialog(false);
    setBulkRows([]);
    setBulkPreview([]);
    setBulkErrors([]);
    setBulkResult(null);
    if (bulkFileRef.current) bulkFileRef.current.value = '';
  };

  const handleScenarioCsvDownload = useCallback(async () => {
    setCsvDownloadLoading(true);
    try {
      let allScenarios: Scenario[] = [...(data?.listScenarios?.items || [])];
      let currentToken = data?.listScenarios?.nextToken;

      while (currentToken) {
        try {
          const result = await fetchMore({
            variables: { companyId, limit: DISPLAY_INCREMENT, nextToken: currentToken },
            updateQuery: (prev, { fetchMoreResult }) => {
              if (!fetchMoreResult) return prev;
              return {
                listScenarios: {
                  __typename: prev.listScenarios.__typename,
                  items: [...prev.listScenarios.items, ...fetchMoreResult.listScenarios.items],
                  nextToken: fetchMoreResult.listScenarios.nextToken,
                },
              };
            },
          });
          if (result.data?.listScenarios?.items) {
            const existingIds = new Set(allScenarios.map((s) => s.scenarioId));
            const uniqueNew = result.data.listScenarios.items.filter(
              (s: Scenario) => !existingIds.has(s.scenarioId)
            );
            allScenarios = [...allScenarios, ...uniqueNew];
          }
          currentToken = result.data?.listScenarios?.nextToken || null;
        } catch (err) {
          console.error('シナリオデータの取得に失敗しました:', err);
          break;
        }
      }

      const scope = currentUser?.managementScope?.trim();
      if (scope) {
        allScenarios = allScenarios.filter((s) => s.department === scope);
      }

      const sorted = sortScenariosByDisplayOrder(allScenarios);

      const csvRows = [
        SCENARIO_CSV_COLUMNS.map((c) => c.label).join(','),
        ...sorted.map((s) =>
          SCENARIO_CSV_COLUMNS.map(({ field: h }) => {
            if (h === 'tags') {
              return stringifyMultiValue(s.tags);
            }
            if (h === 'isActive') {
              return escapeCsvField(s.isActive === undefined ? '' : s.isActive ? '公開' : '非公開');
            }
            if (h === 'showMaterialsOnOrientation') {
              const v = s.showMaterialsOnOrientation !== false;
              return escapeCsvField(v ? 'する' : 'しない');
            }
            if (h === 'estimatedTime') {
              return escapeCsvField(
                s.estimatedTime === undefined || s.estimatedTime === null ? '' : s.estimatedTime
              );
            }
            const val = (s as unknown as Record<string, unknown>)[h];
            if (val === undefined || val === null) {
              return escapeCsvField('');
            }
            return escapeCsvField(String(val));
          }).join(',')
        ),
      ];

      const csvContent = '\uFEFF' + csvRows.join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `scenarios_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('CSVダウンロード中にエラーが発生しました:', err);
      setErrorMessage('CSVダウンロード中にエラーが発生しました。');
    } finally {
      setCsvDownloadLoading(false);
    }
  }, [data?.listScenarios?.items, data?.listScenarios?.nextToken, fetchMore, companyId, currentUser?.managementScope]);

  const handleBulkFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      setBulkErrors(['ファイルサイズが大きすぎます。5MB以下のファイルを選択してください。']);
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const content = String(e.target?.result || '').replace(/^\uFEFF/, '');
        const rows = parseCsv(content);
        if (rows.length < 2) {
          setBulkErrors(['データ行が存在しません']);
          return;
        }
        // 日本語ラベル→内部フィールド名に変換（未知の列はそのまま保持）
        const rawHeader = rows[0].map((h) => h.trim());
        const header = rawHeader.map((h) => SCENARIO_CSV_LABEL_TO_FIELD[h] ?? h);

        const idIdx = header.indexOf('scenarioId');
        if (idIdx < 0) {
          setBulkErrors(['ヘッダーに「シナリオID」列が必要です']);
          return;
        }

        const scope = currentUser?.managementScope?.trim();
        const errors: string[] = [];
        const parsedRows: Array<Record<string, any>> = [];

        rows.slice(1).forEach((row, i) => {
          const lineNo = i + 2;
          const rec: Record<string, any> = {};
          header.forEach((h, idx) => {
            const raw = (row[idx] ?? '').toString();
            if (h === 'scenarioId') {
              rec.scenarioId = raw.trim();
            } else if (h === 'tags') {
              // ブランク → [] で上書き（タグをクリア）
              rec.tags = parseMultiValue(raw);
            } else if (h === 'showMaterialsOnOrientation') {
              const t = raw.trim();
              if (t === '') rec[h] = null; // ブランク → 変更なし
              else if (t === 'する') rec[h] = true;
              else if (t === 'しない') rec[h] = false;
              else errors.push(`${lineNo}行目: 「資料をオリエンテーションで表示」は「する」または「しない」で指定してください`);
            } else if (h === 'isActive') {
              const t = raw.trim();
              if (t === '') rec[h] = null; // ブランク → 変更なし
              else if (t === '公開') rec[h] = true;
              else if (t === '非公開') rec[h] = false;
              else errors.push(`${lineNo}行目: 「公開状態」は「公開」または「非公開」で指定してください`);
            } else if (h === 'estimatedTime') {
              const t = raw.trim();
              if (t === '') {
                errors.push(`${lineNo}行目: 「目安時間（分）」は必須です`);
              } else {
                const n = Number(t);
                if (!Number.isInteger(n) || n < SCENARIO_VALIDATIONS.ESTIMATED_TIME.MIN_VALUE) {
                  errors.push(`${lineNo}行目: 「目安時間（分）」は${SCENARIO_VALIDATIONS.ESTIMATED_TIME.MIN_VALUE}以上の整数で指定してください`);
                } else if (n > SCENARIO_VALIDATIONS.ESTIMATED_TIME.MAX_VALUE) {
                  errors.push(`${lineNo}行目: 「目安時間（分）」は${SCENARIO_VALIDATIONS.ESTIMATED_TIME.MAX_VALUE}分以内で指定してください`);
                } else {
                  rec.estimatedTime = n;
                }
              }
            } else if (h === 'title' || h === 'description' || h === 'goal' || h === 'situation' || h === 'targetName' || h === 'targetGender' || h === 'targetAge' || h === 'targetLanguage') {
              // 必須フィールド: ブランクはエラー
              const requiredLabelMap: Record<string, string> = {
                title: 'タイトル', description: '説明', goal: '目的',
                situation: '状況設定', targetName: '相手の名前',
                targetGender: '相手の性別', targetAge: '相手の年代', targetLanguage: '相手の言語',
              };
              const t = raw.trim();
              if (t === '') {
                errors.push(`${lineNo}行目: 「${requiredLabelMap[h]}」は必須です`);
              } else {
                rec[h] = t;
              }
            } else if (h === 'situationClosed' || h === 'department') {
              // ブランク許可フィールド: '' で上書き可（フィールドをクリア）
              rec[h] = raw;
            } else if (SCENARIO_CSV_FIELD_SET.has(h as any)) {
              // その他（targetGender 等）: ブランク → 変更なし
              rec[h] = raw === '' ? null : raw;
            }
          });

          if (!rec.scenarioId) {
            errors.push(`${lineNo}行目: 「シナリオID」が空です`);
            return;
          }

          // 管理範囲バリデーション：管理範囲が設定されている場合、利用できる所属は管理範囲と一致しなければならない
          if (scope) {
            if (rec.department === '') {
              // ブランクは管理範囲ありのユーザーには不可
              errors.push(`${lineNo}行目: 「利用できる所属」が空です。管理範囲（${scope}）を入力してください`);
            } else if (rec.department !== undefined && rec.department !== scope) {
              errors.push(`${lineNo}行目: 「利用できる所属」は管理範囲（${scope}）以外に変更できません`);
            }
          }

          // 選択肢バリデーション
          if (rec.targetGender != null && !genderOptions.includes(rec.targetGender)) {
            errors.push(`${lineNo}行目: 「相手の性別」の値が無効です（有効値: ${genderOptions.join('、')}）`);
          }
          if (rec.targetAge != null && !ageOptions.includes(rec.targetAge)) {
            errors.push(`${lineNo}行目: 「相手の年代」の値が無効です（有効値: ${ageOptions.join('、')}）`);
          }
          if (rec.targetLanguage != null && !languageOptions.includes(rec.targetLanguage)) {
            errors.push(`${lineNo}行目: 「相手の言語」の値が無効です（有効値: ${languageOptions.join('、')}）`);
          }

          parsedRows.push(rec);
        });

        // 重複 scenarioId チェック
        const idCount: Record<string, number[]> = {};
        parsedRows.forEach((r, i) => {
          if (!idCount[r.scenarioId]) idCount[r.scenarioId] = [];
          idCount[r.scenarioId].push(i + 2);
        });
        Object.entries(idCount).forEach(([id, lines]) => {
          if (lines.length > 1) {
            errors.push(`「シナリオID」が重複しています: ${id}（${lines.join('、')}行目）`);
          }
        });

        if (errors.length > 0) {
          setBulkErrors(errors);
          return;
        }
        setBulkErrors([]);
        setBulkRows(parsedRows);
        setBulkPreview([rawHeader, ...rows.slice(1, 6)]);
        setBulkResult(null);
        setOpenBulkDialog(false);
        setOpenBulkConfirmDialog(true);
      } catch (err) {
        console.error('CSV解析エラー:', err);
        setBulkErrors(['CSVの解析に失敗しました']);
      }
    };
    reader.onerror = () => setBulkErrors(['ファイル読み込みに失敗しました']);
    reader.readAsText(file);
  };

  const handleExecuteBulkUpdate = async () => {
    if (bulkRows.length === 0) return;
    setBulkErrors([]);
    try {
      const res = await bulkUpdateScenarios({
        variables: {
          input: {
            companyId,
            rows: bulkRows.map((r) => ({
              scenarioId: r.scenarioId,
              department: r.department ?? undefined,
              title: r.title ?? undefined,
              description: r.description ?? undefined,
              goal: r.goal ?? undefined,
              estimatedTime: r.estimatedTime ?? undefined,
              situation: r.situation ?? undefined,
              targetName: r.targetName ?? undefined,
              targetGender: r.targetGender ?? undefined,
              targetAge: r.targetAge ?? undefined,
              targetLanguage: r.targetLanguage ?? undefined,
              tags: r.tags ?? undefined,
              isActive: r.isActive ?? undefined,
              showMaterialsOnOrientation: r.showMaterialsOnOrientation ?? undefined,
              situationClosed: r.situationClosed ?? undefined,
            })),
          },
        },
        refetchQueries: [{ query: LIST_SCENARIOS, variables: { companyId, limit: DISPLAY_INCREMENT } }],
      });
      const result = res.data?.bulkUpdateScenarios;
      if (result) {
        setBulkResult(result);
        setOpenBulkConfirmDialog(false);
        setOpenBulkResultDialog(true);
      }
    } catch (err) {
      setBulkErrors([err instanceof Error ? err.message : 'ミューテーション呼び出しに失敗しました']);
    }
  };

  return (
    <Box>
      {/* エラーメッセージ表示 */}
      {errorMessage && (
        <Alert 
          severity="error" 
          sx={{ mb: 2 }}
          onClose={() => setErrorMessage(null)}
        >
          {errorMessage}
        </Alert>
      )}
      {/* データ操作ボタン */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'flex-end', 
        gap: 2, 
        mb: 2,
        mt: 1
      }}>
        <Button
          variant="contained"
          startIcon={
            csvDownloadLoading ? <CircularProgress size={20} color="inherit" /> : <FileDownloadIcon />
          }
          onClick={handleScenarioCsvDownload}
          disabled={csvDownloadLoading || !permCsvDownload}
          sx={{
            bgcolor: theme.palette.smartSkillTalk.linkBlue,
            color: 'white',
            borderRadius: '10px',
            '&:hover': {
              bgcolor: theme.palette.smartSkillTalk.linkBlue,
              opacity: 0.9,
            },
          }}
        >
          {csvDownloadLoading ? 'ダウンロード中...' : 'CSVダウンロード'}
        </Button>
        <Button
          variant="contained"
          startIcon={<FileUploadIcon />}
          onClick={handleOpenBulkDialog}
          disabled={!permCsvImport}
          sx={{
            bgcolor: theme.palette.smartSkillTalk.linkBlue,
            color: 'white',
            borderRadius: '10px',
            '&:hover': {
              bgcolor: theme.palette.smartSkillTalk.linkBlue,
              opacity: 0.9,
            },
          }}
        >
          CSVインポート
        </Button>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
          disabled={!permCreateNew}
          sx={{
            bgcolor: '#5EB600',
            color: 'white',
            borderRadius: '10px',
            '&:hover': {
              bgcolor: '#5EB600',
              opacity: 0.9,
            },
          }}
        >
          新規登録
        </Button>
      </Box>

      {loading && !data ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Paper sx={{ p: 3 }}>
          <Typography color="error">エラーが発生しました: {error.message}</Typography>
        </Paper>
      ) : (
        <ScenarioTable
          scenarios={filteredScenarios}
          nextToken={nextToken}
          loading={loading}
          scenarioRuleCounts={scenarioRuleCounts}
          formatDate={formatDate}
          formatFullDateTime={formatFullDateTime}
          onEdit={handleOpenDialog}
          onDelete={handleOpenDeleteDialog}
          onCustomEvaluation={handleOpenCustomEvaluationDialog}
          onDownload={handleDownloadScenario}
          onToggleActive={handleToggleActive}
          onDragEnd={handleDragEnd}
          onLoadMore={handleLoadMore}
          permCreateNew={permCreateNew}
          permUpdate={permUpdate}
          permDelete={permDelete}
          permCustomScenarioUpdate={permCustomScenarioUpdate}
        />
      )}

      <ScenarioFormDialog
        open={openDialog}
        initialScenario={currentScenario}
        companyId={companyId}
        displayLimit={DISPLAY_INCREMENT}
        saving={createLoading || updateLoading || uploadingFiles}
        onClose={handleCloseDialog}
        onSave={handleSaveScenarioFromDialog}
      />


      {/* 削除確認ダイアログ */}
      <Dialog
        open={openDeleteDialog}
        onClose={(event, reason) => {
          if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') {
            handleCloseDeleteDialog();
          }
        }}
        PaperProps={{
          sx: {
            borderRadius: '10px',
          }
        }}
      >
        <DialogTitle sx={{ 
          bgcolor: '#FF4D4D', 
          color: 'white',
          fontSize: '18px',
          fontWeight: 400
        }}>
          シナリオの削除
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            「{currentScenario?.title}」を削除します。<br/>
            紐づく実施履歴も削除されます。本当に削除しますか？<br/>
            この操作は元に戻せません。
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button 
            onClick={handleDeleteScenario} 
            variant="contained"
            disabled={deleteLoading}
            sx={{ 
              bgcolor: '#FF4D4D',
              color: 'white',
              borderRadius: '6px',
              '&:hover': {
                bgcolor: '#FF4D4D',
                opacity: 0.9
              }
            }}
          >
            {deleteLoading ? <CircularProgress size={24} /> : '削除'}
          </Button>
          <Button 
            onClick={handleCloseDeleteDialog}
            sx={{ 
              color: '#666666',
              borderColor: '#666666',
              borderRadius: '6px',
              '&:hover': {
                borderColor: '#666666',
                opacity: 0.9
              }
            }}
            variant="outlined"
          >
            キャンセル
          </Button>
        </DialogActions>
      </Dialog>

      <CustomEvaluationRulesDialog
        open={openCustomEvaluationDialog}
        scenario={customEvaluationScenario}
        companyId={companyId}
        onClose={handleCloseCustomEvaluationDialog}
        onRulesUpdated={handleCustomEvaluationRulesUpdated}
      />

      {/* CSV インポートダイアログ（ファイル選択） */}
      <Dialog
        open={openBulkDialog}
        onClose={(_, reason) => {
          if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') handleCloseBulkDialog();
        }}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: '10px' } }}
      >
        <DialogTitle sx={{ bgcolor: theme.palette.smartSkillTalk.linkBlue, color: 'white', fontSize: '18px', fontWeight: 400 }}>
          CSVインポート
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <DialogContentText sx={{ mb: 2 }}>
            CSVファイルから既存シナリオを一括更新します（新規登録は不可）。A列に「シナリオID」が必要です。
            <br />
            「シナリオID」と更新したい列以外は、列ごと削除してください。含まれていない列は変更されません。
            <br />
            任意項目において設定内容をクリアしたい場合は、列を残した上でブランクにしてください。
            <br />
            「タグ」列はカンマ区切り（例:「A,B」を1フィールドに）で指定してください。
            <br />
            「目安時間（分）」は1〜{SCENARIO_VALIDATIONS.ESTIMATED_TIME.MAX_VALUE}分の整数、「資料をオリエンテーションで表示」は「する」「しない」、「公開状態」は「公開」「非公開」で指定してください。
          </DialogContentText>
          <Box sx={{ mb: 3 }}>
            <input
              type="file"
              accept=".csv"
              ref={bulkFileRef}
              onChange={handleBulkFileChange}
              style={{ display: 'none' }}
              id="scenario-bulk-file"
            />
            <label htmlFor="scenario-bulk-file">
              <Button
                variant="contained"
                component="span"
                startIcon={<FileUploadIcon />}
                sx={{
                  bgcolor: theme.palette.smartSkillTalk.linkBlue,
                  color: 'white',
                  borderRadius: '10px',
                  '&:hover': { bgcolor: theme.palette.smartSkillTalk.linkBlue, opacity: 0.9 },
                }}
              >
                CSVファイルを選択
              </Button>
            </label>
          </Box>
          {bulkErrors.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Alert severity="error" sx={{ mb: 1 }}>
                CSVファイルにエラーがあります
              </Alert>
              <Paper sx={{ maxHeight: 200, overflow: 'auto', p: 2, bgcolor: '#FFF8F8' }}>
                <ul style={{ margin: 0, paddingLeft: 20 }}>
                  {bulkErrors.map((e, i) => (
                    <li key={i} style={{ color: '#D32F2F', marginBottom: 4 }}>{e}</li>
                  ))}
                </ul>
              </Paper>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={handleCloseBulkDialog}
            variant="outlined"
            sx={{
              color: theme.palette.smartSkillTalk.linkBlue,
              borderColor: theme.palette.smartSkillTalk.linkBlue,
              borderRadius: '6px',
              '&:hover': { borderColor: theme.palette.smartSkillTalk.linkBlue, opacity: 0.9 },
            }}
          >
            キャンセル
          </Button>
        </DialogActions>
      </Dialog>

      {/* CSV インポート確認ダイアログ（プレビュー） */}
      <Dialog
        open={openBulkConfirmDialog}
        onClose={(_, reason) => {
          if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') handleCloseBulkConfirmDialog();
        }}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: '10px' } }}
      >
        <DialogTitle sx={{ bgcolor: theme.palette.smartSkillTalk.linkBlue, color: 'white', fontSize: '18px', fontWeight: 400 }}>
          インポート確認
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <DialogContentText sx={{ mb: 2 }}>
            以下のデータを更新します。よろしいですか？<br />
            （全 {bulkRows.length} 件、先頭 5 件のプレビュー）
          </DialogContentText>
          {bulkErrors.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Alert severity="error" sx={{ mb: 1 }}>
                インポートに失敗しました
              </Alert>
              <Paper sx={{ maxHeight: 200, overflow: 'auto', p: 2, bgcolor: '#FFF8F8' }}>
                <ul style={{ margin: 0, paddingLeft: 20 }}>
                  {bulkErrors.map((e, i) => (
                    <li key={i} style={{ color: '#D32F2F', marginBottom: 4 }}>{e}</li>
                  ))}
                </ul>
              </Paper>
            </Box>
          )}
          <TableContainer component={Paper} sx={{ mb: 2 }}>
            <Table size="small">
              <TableHead sx={{ bgcolor: '#F0F7FF' }}>
                <TableRow>
                  {bulkPreview[0]?.map((h, idx) => (
                    <TableCell key={idx} align="center" sx={{ color: theme.palette.smartSkillTalk.linkBlue, fontSize: '12px', fontWeight: 'bold', whiteSpace: 'nowrap' }}>
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {bulkPreview.slice(1).map((row, rIdx) => (
                  <TableRow key={rIdx} sx={{ bgcolor: rIdx % 2 === 0 ? '#FFFFFF' : '#F9F9F9' }}>
                    {row.map((cell, cIdx) => (
                      <TableCell key={cIdx} align="center" sx={{ fontSize: '12px', whiteSpace: 'nowrap', maxWidth: '150px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {cell}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
            注意: scenarioId が一致する既存シナリオのみ更新されます（新規登録は行われません）。
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={handleExecuteBulkUpdate}
            variant="contained"
            disabled={bulkLoading}
            startIcon={bulkLoading ? <CircularProgress size={20} /> : null}
            sx={{
              bgcolor: theme.palette.smartSkillTalk.linkBlue,
              color: 'white',
              borderRadius: '6px',
              '&:hover': { bgcolor: theme.palette.smartSkillTalk.linkBlue, opacity: 0.9 },
            }}
          >
            {bulkLoading ? '更新中...' : '一括更新を実行'}
          </Button>
          <Button
            onClick={handleCloseBulkConfirmDialog}
            disabled={bulkLoading}
            variant="outlined"
            sx={{
              color: theme.palette.smartSkillTalk.linkBlue,
              borderColor: theme.palette.smartSkillTalk.linkBlue,
              borderRadius: '6px',
              '&:hover': { borderColor: theme.palette.smartSkillTalk.linkBlue, opacity: 0.9 },
            }}
          >
            キャンセル
          </Button>
        </DialogActions>
      </Dialog>

      {/* CSV インポート結果ダイアログ */}
      <Dialog
        open={openBulkResultDialog}
        onClose={(_, reason) => {
          if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') handleCloseBulkResultDialog();
        }}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: '10px' } }}
      >
        <DialogTitle sx={{
          bgcolor: bulkResult?.errorCount ? '#FF4D4D' : theme.palette.smartSkillTalk.linkBlue,
          color: 'white',
          fontSize: '18px',
          fontWeight: 400,
        }}>
          インポート結果
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          {bulkResult && (
            <>
              <Box sx={{ mb: 2 }}>
                <Typography variant="h6" sx={{ mb: 1, color: '#333' }}>処理結果</Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>成功: {bulkResult.successCount} 件</Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>失敗: {bulkResult.errorCount} 件</Typography>
              </Box>
              {bulkResult.errors.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {bulkResult.errors.length} 件のエラーがあります
                  </Alert>
                  <Typography variant="h6" sx={{ mb: 1, color: '#D32F2F' }}>エラー詳細</Typography>
                  <Paper sx={{ maxHeight: 300, overflow: 'auto', p: 2, bgcolor: '#FFF8F8', border: '1px solid #FFCDD2' }}>
                    <ul style={{ margin: 0, paddingLeft: 20 }}>
                      {bulkResult.errors.map((err, i) => (
                        <li key={i} style={{ color: '#D32F2F', marginBottom: 8, lineHeight: 1.5 }}>
                          {err.scenarioId}: {err.message}
                        </li>
                      ))}
                    </ul>
                  </Paper>
                </Box>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={handleCloseBulkResultDialog}
            variant="contained"
            sx={{
              bgcolor: theme.palette.smartSkillTalk.linkBlue,
              color: 'white',
              borderRadius: '6px',
              '&:hover': { bgcolor: theme.palette.smartSkillTalk.linkBlue, opacity: 0.9 },
            }}
          >
            閉じる
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ScenarioManagementPage;
